"use client";

import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { PrivacyPolicyUpdateValidation } from "@/components/providers/privacy-policy-update-validation";
import { VALID_LANGUAGES } from "@/constants/i18n";
import { cookieName } from "@/i18n/settings";
import { useLocalizedRouter } from "@/hooks/use-localized-router";
import { usePersonGetMe } from "@/hooks/use-person-get-me";
import useBoundStore from "@/store/bound-store";
import usePersistedBoundStore from "@/store/persisted-bound-store";
import { buildLocalizedPath, getPathLanguage, resolveCandidateLanguage } from "@/utils/language";
import { cleanRouteParamFromLangPrefix, matchRouteParam } from "@/utils/route";

interface MeGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

const routePatternsThatShouldFetchPersonMe = [
  /^\/process\/.*$/, // Matches process detail routes
  /^\/subprocess\/.*$/, // Matches subprocess detail routes
  /^\/processes$/, // Matches processes dashboard
  /^\/cv\/.*$/, // Matches cv route
  /^\/enhance$/, // Matches enhance routes
  /^\/cv-enhance.*$/, // Matches cv-enhance routes and subroutes
  /^\/evaluation\/.*$/, // Matches evaluation routes
  /^\/settings.*$/, // Matches settings routes
  /^\/autoevaluations\/.*$/, // Matches autoevaluations routes
  /^\/invitation\/.*$/, // Matches invitation routes
  /^\/jobs.*$/, // Matches jobboard routes and subroutes
  /^\/faq.*$/, // Matches faq routes and subroutes
  /^\/cover-letter.*$/, // Matches cover-letter routes and subroutes
  /^\/interviewer.*$/, // Matches interviewer routes and subroutes
  /^\/reports.*$/, // Matches reports routes and subroutes
  /^\/plans.*$/, // Matches plans routes and subroutes
  /^\/documents.*$/, // Matches documents routes and subroutes
  /^\/$/, // Matches root route (home)
];

const COOKIE_MAX_AGE_SECONDS = 31_536_000;

function getCookieValue(name: string): string | null {
  const cookieParts = document.cookie.split("; ");
  const cookiePrefix = `${name}=`;
  const cookie = cookieParts.find((part) => part.startsWith(cookiePrefix));
  if (!cookie) {
    return null;
  }

  return decodeURIComponent(cookie.slice(cookiePrefix.length));
}

function setLanguageCookie(language: string): void {
  document.cookie = `${cookieName}=${language}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
}

function getBackendCandidateLanguage(personData: unknown): string | null {
  return (
    get(personData, "language") ||
    get(personData, "languageCode") ||
    get(personData, "preferredLanguage") ||
    null
  );
}

function getBackendCompanyCountry(personData: unknown): string | null {
  return (
    get(personData, "companyCountry") ||
    get(personData, "company.country") ||
    get(personData, "company.countryCode") ||
    null
  );
}

export function MeGuard({ children, fallback = null }: MeGuardProps) {
  const person = useBoundStore.use.person();
  const setPerson = useBoundStore.use.setPerson();
  const pathname = usePathname();
  const router = useRouter();
  const { navigateTo } = useLocalizedRouter();
  const hasHydrated = usePersistedBoundStore.use.hasHydrated();
  const realm = usePersistedBoundStore.use.realm();

  const [profileValidated, setProfileValidated] = useState(
    person ? get(person, "completeProfile", false) : false
  );

  // Update profileValidated when realm hydrates and becomes "evbasic"
  useEffect(() => {
    if (hasHydrated && realm === "evbasic") {
      setProfileValidated(true);
    }
  }, [hasHydrated, realm]);

  // Clean the pathname from language prefix
  const cleanPath = cleanRouteParamFromLangPrefix(pathname, VALID_LANGUAGES);
  // Check if the current route matches any of the provided patterns
  const shouldFetchPersonMe =
    isEmpty(routePatternsThatShouldFetchPersonMe) ||
    matchRouteParam(cleanPath, routePatternsThatShouldFetchPersonMe);

  const {
    data: personData,
    isFetching,
    isError,
  } = usePersonGetMe({
    enabled: shouldFetchPersonMe && !person,
  });

  // Update person when personData is available
  useEffect(() => {
    if (!personData || person || !hasHydrated) {
      return;
    }

    const nextPerson = personData.data;
    setPerson(nextPerson);
    const urlLanguage = getPathLanguage(pathname ?? "");
    const cookieLanguage = getCookieValue(cookieName);
    const resolvedLanguage = resolveCandidateLanguage({
      manualLanguage: urlLanguage || cookieLanguage,
      candidateLanguage: getBackendCandidateLanguage(nextPerson),
      companyCountry: getBackendCompanyCountry(nextPerson),
      personCountry: get(nextPerson, "country", null),
      acceptLanguage: navigator.language,
    });

    if (!urlLanguage && !cookieLanguage && resolvedLanguage !== "es") {
      localStorage.setItem("i18nextLng", resolvedLanguage);
      setLanguageCookie(resolvedLanguage);
      router?.replace?.(
        `${buildLocalizedPath(pathname ?? "/", resolvedLanguage)}${window.location.search}`
      );
      router?.refresh?.();
      return;
    }

    const completeProfile = get(nextPerson, "completeProfile", false);
    if (!completeProfile && realm === "evcore") {
      navigateTo("/auth/register/normal");
      return;
    }

    // Profile is complete or validation is done
    // For evbasic realm, always set profile as validated since profile completion is not required
    setProfileValidated(true);
  }, [personData, person, setPerson, navigateTo, hasHydrated, realm, pathname, router]);

  // If we shouldn't fetch person data, just render children
  if (!shouldFetchPersonMe) {
    return <>{children}</>;
  }

  // If we have me data and profile validation is complete, render children
  // For evbasic realm, allow rendering as soon as profileValidated is true, even if person is still loading or errored
  if ((person && profileValidated) || (realm === "evbasic" && profileValidated)) {
    return (
      <>
        {realm !== "evbasic" && person && <PrivacyPolicyUpdateValidation user={person} />}
        {children}
      </>
    );
  }

  // If we're fetching or don't have me data, show fallback
  // For evbasic realm, don't block if there's an error (likely token issue) - only wait for profileValidated
  const shouldShowFallback =
    realm === "evbasic"
      ? !profileValidated
      : isFetching || (!person && !isError) || !profileValidated;

  if (shouldShowFallback) {
    return <>{fallback}</>;
  }

  return null;
}
